import { useEffect, useState } from "react";
import { useFlags } from "@unleash/proxy-client-react";
import type { NavigationItemType } from "@/constants/routes/NavItems";
import { User } from "@/models/users/User";
import { Permission } from "@/lib/permissions/Permission.enum";

interface Params {
  user: User | null;
  token: string;
  hasPermission: (opts: {
    allOf?: Permission[];
    oneOf?: Permission[];
  }) => boolean;
  adminNavItems: NavigationItemType[];
  navItems: NavigationItemType[];
  settingsDefaultPath: () => string;
}

const useFilteredNavItems = ({
  user,
  token,
  hasPermission,
  adminNavItems,
  navItems,
  settingsDefaultPath,
}: Params) => {
  const flags = useFlags();
  const [filteredNavItems, setFilteredNavItems] = useState<
    NavigationItemType[]
  >([]);
  const [filteredAdminNavItems, setFilteredAdminNavItems] = useState<
    NavigationItemType[]
  >([]);

  // Same Unleash semantics as the module items in AppSidebarContent: a flag
  // is enabled iff its name is present in the client's toggle list. Flags load
  // async, so `flags` is in the effect deps below to re-filter on arrival.
  const isFlagEnabled = (item: NavigationItemType) =>
    !item.featureFlagName ||
    flags?.find((flag) => flag.name === item.featureFlagName) != undefined;

  useEffect(() => {
    const filterNav = async () => {
      const filtered = navItems.filter(
        (item) =>
          isFlagEnabled(item) &&
          (!item.requiredPermissions || hasPermission(item.requiredPermissions))
      );
      setFilteredNavItems(filtered);
    };
    filterNav();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, flags]);

  useEffect(() => {
    const filterAdmin = async () => {
      const filtered = adminNavItems
        .filter(
          (item) =>
            isFlagEnabled(item) &&
            (!item.requiredPermissions ||
              hasPermission(item.requiredPermissions))
        )
        .map((item) =>
          item.name === "Indstillinger"
            ? { ...item, path: settingsDefaultPath() }
            : item
        );
      setFilteredAdminNavItems(filtered);
    };
    filterAdmin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token, flags]);

  return { filteredNavItems, filteredAdminNavItems };
};

export default useFilteredNavItems;
